import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { normaliseWarehouses } from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import { WarehousesContext, type WarehousesState } from './context.js';
import { fetchWarehouseRows } from './api.js';

/**
 * Every warehouse the vendor publishes, fetched once for the whole app (MYP-30).
 *
 * Above `Layout` rather than inside the Warehouses screen, because three features need the same
 * list and must agree on it: the 仓库地址 page renders it, the Dashboard's ship-to card shows one
 * of them, and Packages resolves a parcel's warehouse name, services and free-storage window from
 * it. Fetching per screen would mean three requests and, on a slow link, three different answers.
 *
 * The raw rows are what is held, not the normalised list. The vendor stores its names, remarks and
 * notices twice — once Chinese, once English — so switching language re-resolves the rows already
 * in memory instead of asking for data that did not change. That also keeps the language toggle
 * instant, which it would not be at this endpoint's speed.
 *
 * Nothing is cached beyond the session and there is no polling: warehouse addresses change perhaps
 * once a year, and a stale address is a parcel sent to the wrong building — so a reload is how it
 * refreshes, which is honest about how fresh the data is.
 */
export function WarehousesProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const [rows, setRows] = useState<readonly unknown[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Guards against the two overlapping-fetch cases at once: StrictMode's dev double-mount, and
    // a reload pressed while the previous attempt is still running. Only the newest effect's
    // result is allowed to land.
    let current = true;

    setRows(undefined);
    setFailed(false);

    fetchWarehouseRows().then(
      (next) => {
        if (current) setRows(next);
      },
      (cause: unknown) => {
        if (!current) return;
        // Logged, not shown. The vendor's failure text is Chinese prose from its own code table:
        // useful to whoever is debugging, not something to put in front of a customer.
        console.error('[warehouses] could not load the warehouse list', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const value = useMemo<WarehousesState>(() => {
    // The vendor has no Malay copy, so ms reads the English side — see `WarehouseLanguage`.
    const warehouses = rows ? normaliseWarehouses(rows, locale === 'zh' ? 'zh' : 'en') : [];

    return {
      warehouses,
      status: rows ? 'ready' : failed ? 'error' : 'loading',
      reload,
    };
  }, [rows, failed, locale, reload]);

  return <WarehousesContext.Provider value={value}>{children}</WarehousesContext.Provider>;
}
